import type { PlayerEvent } from '@mlm/shared';

/**
 * ONE playback contract over two very different backends: an `<video>` element
 * we control directly, and a provider iframe we can only talk to over
 * postMessage. Everything above this line — resume-at-timestamp, the heartbeat,
 * completion detection, keyboard shortcuts, up-next — is written once against
 * this interface instead of twice per backend, which is the whole point of the
 * layer (docs/lms-udemy-roadmap.md, "What we build: a playback adapter").
 */
export type PlaybackStatus = 'idle' | 'handshaking' | 'ready' | 'unsupported';
export type PlaybackState = 'unstarted' | 'playing' | 'paused' | 'buffering' | 'ended';

export type PlayerSnapshot = {
  status: PlaybackStatus;
  state: PlaybackState;
  currentTime: number;
  duration: number | null;
  rate: number;
  rates: number[];
  /** Flips false the first time the provider refuses a rate change (Vimeo). */
  rateSupported: boolean;
  muted: boolean;
  volume: number;
  buffered: Array<[number, number]>;
  captionsAvailable: boolean;
  captionsLang: string | null;
};

export type PlayerCapabilities = {
  rate: boolean;
  volume: boolean;
  captions: boolean;
  fullscreen: 'element' | 'video-native' | 'css' | 'none';
};

export type PlayerListener = (event: PlayerEvent, snapshot: PlayerSnapshot) => void;

export interface PlaybackAdapter {
  /**
   * MUST be invoked synchronously inside the user gesture that authorises it —
   * iOS drops the gesture across an `await`, and un-muting outside a gesture
   * pauses playback outright.
   */
  play(): Promise<void>;
  pause(): void;
  /** `preview` = a scrub in progress (YouTube `seekTo(s, false)`, no re-buffer). */
  seek(seconds: number, opts?: { preview?: boolean }): void;
  /** Resolves false when the provider refused (a creator can disable Vimeo speed). */
  setRate(rate: number): Promise<boolean>;
  setMuted(muted: boolean): void;
  setVolume(volume01: number): void;
  setCaptions(lang: string | null): void;
  getSnapshot(): PlayerSnapshot;
  subscribe(listener: PlayerListener): () => void;
  capabilities: PlayerCapabilities;
  destroy(): void;
}

export const EMPTY_SNAPSHOT: PlayerSnapshot = {
  status: 'idle',
  state: 'unstarted',
  currentTime: 0,
  duration: null,
  rate: 1,
  rates: [],
  rateSupported: true,
  muted: false,
  volume: 1,
  buffered: [],
  captionsAvailable: false,
  captionsLang: null,
};

/** Snapshot store + fan-out shared by both adapters. */
export function createSnapshotStore(initial: Partial<PlayerSnapshot> = {}) {
  let snapshot: PlayerSnapshot = { ...EMPTY_SNAPSHOT, ...initial };
  const listeners = new Set<PlayerListener>();
  return {
    get: () => snapshot,
    patch(next: Partial<PlayerSnapshot>) {
      snapshot = { ...snapshot, ...next };
    },
    emit(event: PlayerEvent) {
      for (const listener of listeners) listener(event, snapshot);
    },
    subscribe(listener: PlayerListener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    clear() {
      listeners.clear();
    },
  };
}

/** Fold a normalized codec event into the snapshot. Shared by both adapters. */
export function applyEvent(
  current: PlayerSnapshot,
  event: PlayerEvent,
): Partial<PlayerSnapshot> | null {
  switch (event.type) {
    case 'ready':
      return current.status === 'ready' ? null : { status: 'ready' };
    case 'state':
      return { state: event.state === 'cued' ? 'unstarted' : event.state };
    case 'time':
      return {
        currentTime: event.currentTime,
        ...(event.duration != null && event.duration > 0 ? { duration: event.duration } : {}),
        ...(event.rate != null ? { rate: event.rate } : {}),
      };
    case 'seeked':
      return { currentTime: event.currentTime };
    case 'duration':
      return event.duration > 0 ? { duration: event.duration } : null;
    case 'rate':
      return { rate: event.rate };
    case 'rates':
      return { rates: event.rates, rateSupported: event.rates.length > 0 };
    default:
      return null;
  }
}
